/**
 * @voltxampere/ui — StampTool (Sprint 13)
 *
 * Place sembols by following the cursor with a translucent
 * preview ghost. Activated by sidebar card click or single-letter
 * shortcut (R / C / L / D / …).
 *
 *   pointermove → ghost SceneNode position updates (snap to grid)
 *   pointerdown → PlaceCommand, swap to SelectTool unless Shift
 *                 lock-mode is held
 *   R           → rotate ghost +90°
 *   Shift down  → enable lockMode (consecutive places)
 *   Shift up    → disable lockMode
 *   Esc         → cancel, swap to SelectTool
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { PlaceCommand } from "../commands/index.js";
export const STAMP_GRID_SIZE = 16;
export class StampTool {
    id = "stamp";
    cursor = "crosshair";
    template = null;
    rotation = 0;
    lockMode = false;
    cursorWorld = null;
    serial = 1;
    ghostNodeId = "vxa-stamp-ghost";
    onExit;
    constructor(opts = {}) {
        if (opts.onExit !== undefined)
            this.onExit = opts.onExit;
    }
    setTemplate(template) {
        this.template = template;
        this.rotation = 0;
    }
    onPointerMove(e, ctx) {
        if (!this.template)
            return;
        const world = ctx.worldFromEvent(e);
        this.cursorWorld = ctx.viewport.snapToGrid(world, STAMP_GRID_SIZE);
        ctx.invalidate();
    }
    onPointerDown(e, ctx) {
        if (e.button !== 0)
            return;
        if (!this.template)
            return;
        const circuit = ctx.circuit.value;
        if (!circuit)
            return;
        const world = ctx.worldFromEvent(e);
        const position = ctx.viewport.snapToGrid(world, STAMP_GRID_SIZE);
        const id = this.makeId();
        // Sprint 38-1 — variant params: shallow-copy the template's
        // datasheet overrides into instance.params. Empty object when
        // template.params is absent (Sprint 13 legacy parity).
        const seedParams = this.template.params !== undefined ? { ...this.template.params } : {};
        const instance = {
            id,
            type: this.template.type,
            position,
            rotation: this.rotation,
            flipped: false,
            params: seedParams,
            terminals: [],
            ...(this.template.defaultLabel !== undefined
                ? { label: `${this.template.defaultLabel}${String(this.serial++)}` }
                : {}),
        };
        ctx.circuit.value = ctx.undoStack.execute(new PlaceCommand(instance), circuit);
        if (!this.lockMode) {
            this.exit(ctx);
        }
        else {
            ctx.invalidate();
        }
    }
    onKeyDown(e, ctx) {
        if (e.key === "r" || e.key === "R") {
            this.rotation = ((this.rotation + 90) % 360);
            ctx.invalidate();
            e.preventDefault();
            return;
        }
        if (e.key === "Shift") {
            this.lockMode = true;
            return;
        }
        if (e.key === "Escape") {
            this.exit(ctx);
        }
    }
    onKeyUp(e, ctx) {
        if (e.key === "Shift") {
            this.lockMode = false;
            ctx.invalidate();
        }
    }
    onLeave(ctx) {
        this.cursorWorld = null;
        ctx.invalidate();
    }
    /** Helper for the renderer — emit ghost SceneNode. */
    ghostNodes() {
        if (!this.template || !this.cursorWorld)
            return [];
        return [
            {
                kind: "ghost",
                id: this.ghostNodeId,
                template: { type: this.template.type },
                position: this.cursorWorld,
                rotation: this.rotation,
            },
        ];
    }
    /** Test surface. */
    get currentRotation() {
        return this.rotation;
    }
    /** Test surface. */
    get isLocked() {
        return this.lockMode;
    }
    /** Test surface. */
    get currentTemplate() {
        return this.template;
    }
    makeId() {
        if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
            return crypto.randomUUID();
        }
        return `id-${Math.random().toString(36).slice(2)}`;
    }
    exit(ctx) {
        this.template = null;
        this.cursorWorld = null;
        this.rotation = 0;
        this.lockMode = false;
        ctx.invalidate();
        if (this.onExit)
            this.onExit();
    }
}
//# sourceMappingURL=stamp.js.map